# Sharing a candidate

The details of a candidate can be uploaded to the SEEK API.
This information is used to identify candidates that exist in your software and in SEEK’s Talent Search database when returning search results,
reducing the time hirers spend cross-referencing candidate information with your software.
Candidates that exist in both systems are referred to as shared candidates.

Shared candidates shown on the Talent Search website will have a badge indicating that they are shared,
the date the candidate was last updated in your software and on SEEK,
and a deep link back to your software.

Talent Search requires that changes to candidate information within your software be reflected in the SEEK API [within 60 seconds].

![Shared candidate results](./assets/SharedCandidatesSERP.svg 'Shared & SEEK Profiles =style=none')

[within 60 seconds]: https://au.employer.seek.com/partners/terms-of-use#talent-search-connect

## Data structure

An uploaded [`Candidate`] object is structured as follows:

```mermaid Uploaded candidate objects
erDiagram
        Candidate ||--|| CandidatePerson : specifies
        Candidate ||--|| CandidateProfile : contains
        Candidate ||--|| HiringOrganization : "belongs to"
```

An uploaded candidate has the following [`CandidatePerson`] fields:

- Name
- Email addresses, with at least one required
- Optional phone numbers
- Optional home locations
- Whether they can be contacted

Each uploaded candidate has a single [`CandidateProfile`] containing:

- Create and update dates
- Profile actions that deep link into your software
- Preferred work locations
- [Interaction history]

Each uploaded candidate is scoped to a specific SEEK hirer and integration partner.
A candidate uploaded via your software is only accessible to that hirer,
and is not accessible to other integration partners who may have a relationship with the hirer.

## Operations

### Upload a candidate

The [`uploadCandidate` mutation] adds a new candidate and their profile.
You should use this mutation whenever a new candidate is created in your software.
This includes candidates you create in your software using [profiles purchased] from Talent Search.

To avoid unintentional duplication of data, SEEK enforces uniqueness on an email address of the candidate.
Requests to share a candidate require a [`seekPrimaryEmailAddress` field] with a value that matches one of the given email address values in the [`person.communication` field].
A request to upload an existing candidate will receive an `UploadCandidatePayload_Conflict` result.
The existing candidate will not be updated; you can select their details from the conflict result.

If a candidate has expressed a preference to not be contacted,
you should upload them with a `seekDoNotContactIndicator` value of `true` in the [`person.communication` field].
This will customise the display name of the candidate in SEEK Talent Search and prevent hirers from contacting them.

| `seekDoNotContactIndicator` | Display name                                             |
| :-------------------------: | :------------------------------------------------------- |
|           `null`            | Hana Webber                                              |
|           `false`           | Hana Webber                                              |
|           `true`            | Hana Webber has elected not to be contacted by `Partner` |

Up to 20 [interaction history] items can be provided in the `seekProcessHistory` field during initial upload.
Individual items can be later created, modified and deleted using the relevant [interaction history operations].
SEEK strongly recommends providing the first 20 interaction history items when backfilling an existing candidate.
This maximises the rate your software can backfill candidates while reducing load on SEEK’s systems.

Keep track of the IDs assigned to the candidate, their profile and their interaction history to perform subsequent update and delete operations.
The [`UploadCandidatePayload_Success.candidateProcessHistoryItems` field] contains the uploaded interaction history in input order for ease of correlation.

```graphql label="Mutation"
mutation ($input: UploadCandidateInput!) {
  uploadCandidate(input: $input) {
    ... on UploadCandidatePayload_Success {
      candidate {
        documentId {
          value
        }
        profiles {
          profileId {
            value
          }
        }
      }
      candidateProcessHistoryItems {
        id {
          value
        }
      }
    }

    ... on UploadCandidatePayload_Conflict {
      conflictingCandidate {
        documentId {
          value
        }
        profiles {
          profileId {
            value
          }
        }
      }
    }
  }
}
```

```json label="Variables"
{
  "input": {
    "candidate": {
      "seekPrimaryEmailAddress": "Marcus_Damore@gmail.com",
      "person": {
        "communication": {
          "address": [
            {
              "city": "Melbourne",
              "countryCode": "AU",
              "countrySubDivisions": [{ "type": "State", "value": "AU-VIC" }],
              "extendedLines": [
                { "type": "Apartment", "value": "Apartment 5" }
              ],
              "formattedAddress": "Apartment 3, 5 Fake St\nWindsor VIC 3181",
              "geoLocation": {
                "latitude": -37.854,
                "longitude": 144.988
              },
              "line": "5 Fake St",
              "postalCode": "3181"
            }
          ],
          "seekDoNotContactIndicator": false,
          "email": [
            {
              "address": "Marcus_Damore@gmail.com"
            }
          ],
          "phone": []
        },
        "name": {
          "formattedName": "Marcus Damore",
          "family": "Damore",
          "given": "Marcus"
        }
      },
      "distributionGuidelines": {
        "seekProductCodes": ["ProactiveSourcing"]
      }
    },
    "candidateProfile": {
      "createDateTime": "2006-01-02T15:04:05Z",
      "updateDateTime": "2020-08-08T15:04:05.000Z",
      "seekProcessHistory": [],
      "positionPreferences": [],
      "seekActions": [
        {
          "code": "ViewProfile",
          "seekUrl": {
            "url": "https://example.com/profiles/a1b2c3d4"
          }
        }
      ]
    },
    "hiringOrganization": {
      "id": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  }
}
```

```jsonc label="Success result"
{
  "uploadCandidate": {
    // Details of the newly uploaded candidate
    "candidate": {
      "documentId": {
        "value": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
      },
      "profiles": [
        {
          "profileId": {
            "value": "globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV"
          }
        }
      ]
    },
    "candidateProcessHistoryItems": []
  }
}
```

```jsonc label="Conflict result"
{
  "uploadCandidate": {
    // Details of the conflicting candidate
    "conflictingCandidate": {
      "documentId": {
        "value": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
      },
      "profiles": [
        {
          "profileId": {
            "value": "globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV"
          }
        }
      ]
    }
  }
}
```

[`seekprimaryemailaddress` field]: /schema/named-type/UploadCandidate_CandidateInput#/field/seekPrimaryEmailAddress
[`person.communication` field]: /schema/named-type/CandidatePersonInput#/field/communication
[interaction history operations]: /use-cases/proactive-sourcing/interaction-history#operations

### Update an uploaded candidate

The following mutations update an uploaded candidate and their profile.
You should use these mutations whenever an existing candidate is updated in your software.
This includes candidates you update in your software using [profiles purchased] from Talent Search.

- The [`updateUploadedCandidatePerson` mutation] replaces personal details.

  A request to update the candidate’s email with an existing `seekPrimaryEmailAddress` will receive a `UpdateUploadedCandidatePersonPayload_Conflict`.
  The existing candidate will not be updated; you can select the conflicting candidate details from the conflict result.

- The [`updateUploadedCandidateProfileActions` mutation] replaces profile actions.

- The [`updateUploadedCandidateProfileDates` mutation] replaces profile create and update dates.

  The profile’s update date is intended to reflect when the candidate has been updated in your software.
  This mutation should be used even if the update isn’t captured by the profile fields you upload to SEEK.

- The [`updateUploadedCandidateProfilePositionPreferences` mutation] replaces profile position preferences.

These mutations can be [batched] together in a single request for sync-like behaviour:

```graphql label="Mutation"
mutation updateUploadedCandidate(
  $documentId: String!
  $person: CandidatePersonInput!
  $seekPrimaryEmailAddress: String!
  $profileId: String!
  $seekActions: [CandidateProcessActionInput!]!
  $createDateTime: DateTime!
  $updateDateTime: DateTime!
  $positionPreferences: [PositionPreferenceInput!]!
) {
  updateUploadedCandidatePerson(
    input: {
      candidate: {
        documentId: $documentId
        person: $person
        seekPrimaryEmailAddress: $seekPrimaryEmailAddress
      }
    }
  ) {
    ... on UpdateUploadedCandidatePersonPayload_Success {
      candidate {
        documentId {
          value
        }
      }
    }

    ... on UpdateUploadedCandidatePersonPayload_Conflict {
      conflictingCandidate {
        documentId {
          value
        }
      }
    }
  }

  updateUploadedCandidateProfileActions(
    input: {
      candidateProfile: { profileId: $profileId, seekActions: $seekActions }
    }
  ) {
    candidateProfile {
      candidate {
        documentId {
          value
        }
      }
    }
  }

  updateUploadedCandidateProfileDates(
    input: {
      candidateProfile: {
        profileId: $profileId
        createDateTime: $createDateTime
        updateDateTime: $updateDateTime
      }
    }
  ) {
    candidateProfile {
      candidate {
        documentId {
          value
        }
      }
    }
  }

  updateUploadedCandidateProfilePositionPreferences(
    input: {
      candidateProfile: {
        profileId: $profileId
        positionPreferences: $positionPreferences
      }
    }
  ) {
    candidateProfile {
      candidate {
        documentId {
          value
        }
      }
    }
  }
}
```

```json label="Variables"
{
  "documentId": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha",
  "seekPrimaryEmailAddress": "Marcus_Damore@newDomain.com",
  "person": {
    "communication": {
      "email": [
        {
          "address": "Marcus_Damore@newDomain.com"
        }
      ],
      "phone": []
    },
    "name": {
      "formattedName": "Mars Damore",
      "family": "Damore",
      "given": "Mars"
    }
  },
  "profileId": "globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV",
  "seekActions": [],
  "createDateTime": "1970-01-01T00:00:00Z",
  "updateDateTime": "2020-12-31T23:59:59.999Z",
  "positionPreferences": [
    {
      "locations": [
        {
          "referenceLocation": {
            "city": "Melbourne",
            "countryCode": "AU",
            "countrySubDivisions": [],
            "extendedLines": [],
            "postalCode": "3000"
          }
        }
      ]
    }
  ]
}
```

```json label="Success result"
{
  "updateUploadedCandidatePerson": {
    "candidate": {
      "documentId": {
        "value": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
      }
    }
  },
  "updateUploadedCandidateProfileActions": {
    "candidatePerson": {
      "candidate": {
        "documentId": {
          "value": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
        }
      }
    }
  },
  "updateUploadedCandidateProfileDates": {
    "candidatePerson": {
      "candidate": {
        "documentId": {
          "value": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
        }
      }
    }
  },
  "updateUploadedCandidateProfilePositionPreferences": {
    "candidatePerson": {
      "candidate": {
        "documentId": {
          "value": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
        }
      }
    }
  }
}
```

```json label="Conflict result"
{
  "updateUploadedCandidatePerson": {
    "conflictingCandidate": {
      "documentId": {
        "value": "globalPublicTest:candidate:uploaded:LupBTN3BD6NTiuS419iRCa"
      }
    }
  },
  "updateUploadedCandidateProfileActions": {
    "candidatePerson": {
      "candidate": {
        "documentId": {
          "value": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
        }
      }
    }
  },
  "updateUploadedCandidateProfileDates": {
    "candidatePerson": {
      "candidate": {
        "documentId": {
          "value": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
        }
      }
    }
  },
  "updateUploadedCandidateProfilePositionPreferences": {
    "candidatePerson": {
      "candidate": {
        "documentId": {
          "value": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
        }
      }
    }
  }
}
```

### Delete an uploaded candidate

The [`deleteUploadedCandidate` mutation] deletes an uploaded candidate and their profile.

This will erase all uploaded details of the candidate from SEEK’s systems.

```graphql
mutation ($input: DeleteUploadedCandidateInput!) {
  deleteUploadedCandidate(input: $input) {
    candidate {
      documentId {
        value
      }
      profiles {
        profileId {
          value
        }
      }
    }
  }
}
```

```json
{
  "input": {
    "candidate": {
      "documentId": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
    }
  }
}
```

```json
{
  "deleteUploadedCandidate": {
    "candidate": {
      "documentId": {
        "value": "globalPublicTest:candidate:uploaded:12uNaG7vqgW6G1jG7qp7ha"
      },
      "profiles": [
        {
          "profileId": {
            "value": "globalPublicTest:candidateProfile:uploaded:7m7JgSEM2rkAwrxoBrfPyV"
          }
        }
      ]
    }
  }
}
```

[batched]: /graphql/in-practice#operation-batching
[interaction history]: /use-cases/proactive-sourcing/interaction-history
[`candidate`]: /schema/named-type/Candidate
[`candidateperson`]: /schema/named-type/CandidatePerson
[`candidateprofile`]: /schema/named-type/CandidateProfile
[`uploadcandidate` mutation]: /schema/mutation/uploadCandidate
[`uploadcandidatepayload_success.candidateprocesshistoryitems` field]: /schema/named-type/UploadCandidatePayload_Success#/field/candidateProcessHistoryItems
[`updateuploadedcandidateperson` mutation]: /schema/mutation/updateUploadedCandidatePerson
[`updateuploadedcandidateprofileactions` mutation]: /schema/mutation/updateUploadedCandidateProfileActions
[`updateuploadedcandidateprofiledates` mutation]: /schema/mutation/updateUploadedCandidateProfileDates
[`updateuploadedcandidateprofilepositionpreferences` mutation]: /schema/mutation/updateUploadedCandidateProfilePositionPreferences
[`deleteuploadedcandidate` mutation]: /schema/mutation/deleteUploadedCandidate
[profiles purchased]: /use-cases/proactive-sourcing/profile-purchases
